import React from 'react';
import classNames from 'classnames';

import PrimaryButton from './buttons/PrimaryButton';
import { formatCurrency } from '../../lib/utils/formatters';

const formatFeePct = (pct) => `${Number(pct).toFixed(2)}%`;

/**
 * Section card that renders the server-approved biweekly (Pay in 4) term
 * following the ui-elements multi-decision flow pattern. All values come from
 * the approved_terms data — nothing is fabricated in the UI. Selecting the
 * card expands an inline Continue button.
 *
 * Props:
 *   term             – Immutable Map for the available biweekly ApprovedTerm
 *   selectedTerm     – { term, term_period, payment } from parent state
 *   setSelectedTerm  – parent state setter
 *   onContinue       – called when the user clicks Continue (same as Terms.js `save`)
 *   cart             – shopping cart entity from Redux
 *   isLoading        – disables the Continue button while routing
 */
export default function PayIn4Card({
  term,
  selectedTerm,
  setSelectedTerm,
  onContinue,
  cart,
  isLoading,
  showRateBadge = true,
}) {
  const numPayments = term.get('term');
  const payment = term.get('monthly_payment');
  const fees = parseFloat(term.get('total_fees'));

  const isSelected =
    selectedTerm?.term === numPayments &&
    selectedTerm?.term_period === 'biweekly';

  const handleSelect = () => {
    if (!isSelected) {
      setSelectedTerm({
        term: numPayments,
        term_period: term.get('term_period'),
        payment,
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.target !== e.currentTarget) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSelect();
    }
  };

  const badgeLabel = () => {
    if (fees === 0) return 'No fee';
    const grandTotal = parseFloat(cart?.grand_total);
    const pct = grandTotal > 0 ? (fees / grandTotal) * 100 : 0;
    return `${formatFeePct(pct)} fee`;
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      className={classNames(
        'mb-4 border rounded-2xl p-5 bg-white shadow-sm transition-all',
        isSelected
          ? 'border-[3px] border-primary-blue'
          : 'border-zinc-300 hover:border-blue-300 cursor-pointer'
      )}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-1">
        <div>
          <h2 className="text-base sm:text-lg font-bold">Pay in 4</h2>
          <p className="text-xs sm:text-sm text-secondary-gray mt-0.5">
            {numPayments} interest-free installments, every 2 weeks.
          </p>
        </div>
        {showRateBadge && (
          <span
            className={classNames(
              'text-xs font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ml-3',
              fees === 0
                ? 'bg-background-success text-success border-green-200'
                : 'bg-blue-50 text-primary-blue border-blue-200'
            )}
          >
            {badgeLabel()}
          </span>
        )}
      </div>

      {/* Payment summary */}
      <div className="flex items-center justify-between text-sm pt-3 mt-3 border-t border-primary-border-gray">
        <span className="text-secondary-gray">
          {numPayments} payments of {formatCurrency(payment)}
        </span>
        <span className="font-semibold">{formatCurrency(payment)} today</span>
      </div>

      {/* Continue — only visible when the card is selected */}
      {isSelected && (
        <div className="mt-4">
          <PrimaryButton
            buttonText="Continue"
            buttonColor="bg-primary-blue"
            hoverClass="hover:bg-primary-blue-hover"
            disabled={isLoading}
            isLoading={isLoading}
            onClick={onContinue}
          />
        </div>
      )}
    </div>
  );
}
